import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

const LightboxContext = createContext(null);

export function useLightbox() {
  return useContext(LightboxContext);
}

export function LightboxProvider({ children }) {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  const openLightbox = useCallback((list, startIndex = 0) => {
    const normalized = Array.isArray(list) ? list : [list];
    setItems(normalized);
    setIndex(Math.max(0, Math.min(startIndex, normalized.length - 1)));
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  const next = useCallback(
    () => setIndex((i) => (i + 1) % Math.max(items.length, 1)),
    [items.length]
  );
  const prev = useCallback(
    () =>
      setIndex((i) => (i - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1)),
    [items.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, next, prev]);

  const current = items[index];

  return (
    <LightboxContext.Provider value={{ openLightbox, close }}>
      {children}
      {open && current && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-in fade-in"
          onClick={close}
        >
          <button
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>

          {items.length > 1 && (
            <>
              <button
                aria-label="Previous"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                aria-label="Next"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <div
            className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={current.image}
              src={current.image}
              alt={current.title || "Photo"}
              style={
                current.rotate
                  ? { transform: `rotate(${current.rotate}deg)` }
                  : undefined
              }
              className={
                current.rotate
                  ? "max-w-[85vh] max-h-[92vw] object-contain rounded-lg shadow-2xl"
                  : "max-w-[92vw] max-h-[76vh] object-contain rounded-lg shadow-2xl fade-in"
              }
            />
            {(current.title || current.caption) && (
              <div className="text-center px-4">
                {current.title && (
                  <div className="font-display font-bold text-[16px] text-[#f4efe6]">
                    {current.title}
                  </div>
                )}
                {current.caption && (
                  <div className="text-[13px] text-[#c8c5bc] mt-1">
                    {current.caption}
                  </div>
                )}
                {items.length > 1 && (
                  <div className="text-[11.5px] tracking-[0.18em] uppercase text-[#8a877f] mt-2">
                    {index + 1} / {items.length}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </LightboxContext.Provider>
  );
}
